import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { DecisionConfirmModal } from '../shared/index.js';
import { confidencePercent } from '../../../utils/detailFormat.js';
import { ArrowUpCircle } from 'lucide-react';

/**
 * Gov Promotion decision for interim `proposal` events.
 *
 * Prototype-aligned promote confirm modal (Train Me / Gov Promotion family).
 * Backend attestation endpoint is not wired yet (CT-4867) — confirm shows an
 * informational toast instead of a false success.
 */
function ProposalDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const detail = event.detail ?? {};
  const proposed = detail.proposedStatement ?? event.situation ?? '';
  const current = detail.currentStatement ?? null;
  const confidence = confidencePercent(detail.confidence);

  const promoteRows = useMemo(
    () =>
      [
        { label: t('proposal.factTitle'), value: event.title },
        detail.knowledgebaseName
          ? { label: t('proposal.knowledgeBase'), value: detail.knowledgebaseName }
          : null,
        current
          ? { label: t('proposal.change'), value: `${current} → ${proposed}` }
          : { label: t('proposal.proposedValue'), value: proposed },
        confidence ? { label: t('proposal.confidence'), value: confidence } : null,
        detail.riskLevel ? { label: t('proposal.risk'), value: detail.riskLevel } : null,
      ].filter((row) => row?.value),
    [t, event.title, detail, proposed, current, confidence],
  );

  const handleClose = useCallback(() => setConfirming(false), []);

  const handlePromote = useCallback(() => {
    toast.info({
      title: t('proposal.comingSoonTitle'),
      message: t('proposal.comingSoonMessage'),
    });
    handleClose();
  }, [handleClose, t, toast]);

  const primaryActions = useMemo(
    () => [
      {
        id: 'promote',
        label: t('proposal.promote'),
        size: 'md',
        icon: ArrowUpCircle,
        onClick: () => setConfirming(true),
      },
    ],
    [t],
  );

  return (
    <>
      <DecisionFooter
        event={event}
        primaryActions={primaryActions}
        onSkip={actions.skip}
        onAsk={actions.ask}
        onEscalate={actions.escalate}
        onTrace={actions.trace}
        extraMenuItems={actions.extraMenuItems}
        sticky={actions.sticky}
      />

      <DecisionConfirmModal
        isOpen={confirming}
        onClose={handleClose}
        icon={<ArrowUpCircle size={20} className="text-violet-400" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('proposal.promoteTitle')}
        subtitle={t('proposal.promoteSubtitle')}
        summaryRows={promoteRows}
        showReason
        reasonLabel={t('proposal.noteLabel')}
        reasonPlaceholder={t('proposal.notePlaceholder')}
        confirmLabel={t('proposal.promote')}
        onConfirm={handlePromote}
      />
    </>
  );
}

export default ProposalDecision;
